/**
 * i18n Configuration - Internationalization Setup
 * 
 * Uses react-i18next for localization support.
 * Currently supports: English (en), Korean (ko), Chinese Simplified (zh-CN), Portuguese-BR (pt-BR), Spanish (es),
 *   German (de), French (fr), Italian (it), Dutch (nl), Swedish (sv), Polish (pl)
 * 
 * Translation files are organized by namespace:
 * - common: Shared strings (buttons, labels, errors)
 * - chat: Chat interface strings
 * - profile: Profile panel strings
 * - checkout: Checkout flow strings
 * - admin: Admin panel strings
 * - modals: Modal dialogs strings
 * 
 * Features:
 * - Browser language detection with localStorage persistence
 * - RTL support for future Arabic, Hebrew, Farsi, Urdu languages
 * - Fallback to English when translations are missing
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// RTL languages for future support
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

/**
 * Apply language and direction attributes to the HTML document
 * @param lng - Language code
 */
function applyHtmlLangDir(lng: string): void {
  const dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', lng);
  document.documentElement.setAttribute('dir', dir);
}

// Import translation files
import enCommon from './locales/en/common.json';
import enChat from './locales/en/chat.json';
import enProfile from './locales/en/profile.json';
import enCheckout from './locales/en/checkout.json';
import enAdmin from './locales/en/admin.json';
import enModals from './locales/en/modals.json';

import koCommon from './locales/ko/common.json';
import koChat from './locales/ko/chat.json';
import koProfile from './locales/ko/profile.json';
import koCheckout from './locales/ko/checkout.json';
import koAdmin from './locales/ko/admin.json';
import koModals from './locales/ko/modals.json';

import ptBRCommon from './locales/pt-BR/common.json';
import ptBRChat from './locales/pt-BR/chat.json';
import ptBRProfile from './locales/pt-BR/profile.json';
import ptBRCheckout from './locales/pt-BR/checkout.json';
import ptBRAdmin from './locales/pt-BR/admin.json';
import ptBRModals from './locales/pt-BR/modals.json';

import esCommon from './locales/es/common.json';
import esChat from './locales/es/chat.json';
import esProfile from './locales/es/profile.json';
import esCheckout from './locales/es/checkout.json';
import esAdmin from './locales/es/admin.json';
import esModals from './locales/es/modals.json';

import deCommon from './locales/de/common.json';
import deChat from './locales/de/chat.json';
import deProfile from './locales/de/profile.json';
import deCheckout from './locales/de/checkout.json';
import deAdmin from './locales/de/admin.json';
import deModals from './locales/de/modals.json';

import frCommon from './locales/fr/common.json';
import frChat from './locales/fr/chat.json';
import frProfile from './locales/fr/profile.json';
import frCheckout from './locales/fr/checkout.json';
import frAdmin from './locales/fr/admin.json';
import frModals from './locales/fr/modals.json';

import itCommon from './locales/it/common.json';
import itChat from './locales/it/chat.json';
import itProfile from './locales/it/profile.json';
import itCheckout from './locales/it/checkout.json';
import itAdmin from './locales/it/admin.json';
import itModals from './locales/it/modals.json';

import nlCommon from './locales/nl/common.json';
import nlChat from './locales/nl/chat.json';
import nlProfile from './locales/nl/profile.json';
import nlCheckout from './locales/nl/checkout.json';
import nlAdmin from './locales/nl/admin.json';
import nlModals from './locales/nl/modals.json';

import svCommon from './locales/sv/common.json';
import svChat from './locales/sv/chat.json';
import svProfile from './locales/sv/profile.json';
import svCheckout from './locales/sv/checkout.json';
import svAdmin from './locales/sv/admin.json';
import svModals from './locales/sv/modals.json';

import plCommon from './locales/pl/common.json';
import plChat from './locales/pl/chat.json';
import plProfile from './locales/pl/profile.json';
import plCheckout from './locales/pl/checkout.json';
import plAdmin from './locales/pl/admin.json';
import plModals from './locales/pl/modals.json';

import zhCNCommon from './locales/zh-CN/common.json';
import zhCNChat from './locales/zh-CN/chat.json';
import zhCNProfile from './locales/zh-CN/profile.json';
import zhCNCheckout from './locales/zh-CN/checkout.json';
import zhCNAdmin from './locales/zh-CN/admin.json';
import zhCNModals from './locales/zh-CN/modals.json';

// Define resources
const resources = {
  en: {
    common: enCommon,
    chat: enChat,
    profile: enProfile,
    checkout: enCheckout,
    admin: enAdmin,
    modals: enModals,
  },
  ko: {
    common: koCommon,
    chat: koChat,
    profile: koProfile,
    checkout: koCheckout,
    admin: koAdmin,
    modals: koModals,
  },
  'pt-BR': {
    common: ptBRCommon,
    chat: ptBRChat,
    profile: ptBRProfile,
    checkout: ptBRCheckout,
    admin: ptBRAdmin,
    modals: ptBRModals,
  },
  es: {
    common: esCommon,
    chat: esChat,
    profile: esProfile,
    checkout: esCheckout,
    admin: esAdmin,
    modals: esModals,
  },
  de: {
    common: deCommon,
    chat: deChat,
    profile: deProfile,
    checkout: deCheckout,
    admin: deAdmin,
    modals: deModals,
  },
  fr: {
    common: frCommon,
    chat: frChat,
    profile: frProfile,
    checkout: frCheckout,
    admin: frAdmin,
    modals: frModals,
  },
  it: {
    common: itCommon,
    chat: itChat,
    profile: itProfile,
    checkout: itCheckout,
    admin: itAdmin,
    modals: itModals,
  },
  nl: {
    common: nlCommon,
    chat: nlChat,
    profile: nlProfile,
    checkout: nlCheckout,
    admin: nlAdmin,
    modals: nlModals,
  },
  sv: {
    common: svCommon,
    chat: svChat,
    profile: svProfile,
    checkout: svCheckout,
    admin: svAdmin,
    modals: svModals,
  },
  pl: {
    common: plCommon,
    chat: plChat,
    profile: plProfile,
    checkout: plCheckout,
    admin: plAdmin,
    modals: plModals,
  },
  'zh-CN': {
    common: zhCNCommon,
    chat: zhCNChat,
    profile: zhCNProfile,
    checkout: zhCNCheckout,
    admin: zhCNAdmin,
    modals: zhCNModals,
  },
};

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'ko', 'zh-CN', 'pt-BR', 'es', 'de', 'fr', 'it', 'nl', 'sv', 'pl'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Default namespace
export const DEFAULT_NAMESPACE = 'common';

// Migrate legacy localStorage key (skinmatch-language → heseda-ai-language)
try {
  const oldKey = 'skinmatch-language';
  const newKey = 'heseda-ai-language';
  const oldVal = localStorage.getItem(oldKey);
  if (oldVal && !localStorage.getItem(newKey)) {
    localStorage.setItem(newKey, oldVal);
    localStorage.removeItem(oldKey);
  }
} catch { /* ignore in SSR/private browsing */ }

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: DEFAULT_NAMESPACE,
    ns: ['common', 'chat', 'profile', 'checkout', 'admin', 'modals'],
    
    // Language detection options
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'heseda-ai-language',
      caches: ['localStorage'],
    },
    
    interpolation: {
      escapeValue: false, // React already escapes
    },
    
    // Debug mode in development
    debug: import.meta.env.DEV,
    
    // React options
    react: {
      useSuspense: false, // Disable suspense for now
    },
  });

// Apply initial language direction
applyHtmlLangDir(i18n.language);

// Update HTML attributes on language change
i18n.on('languageChanged', (lng) => {
  applyHtmlLangDir(lng);
});

/**
 * Change the current language
 * - Saves to localStorage for persistence across sessions
 * - Updates i18next internal state to trigger re-renders
 * - Updates HTML lang and dir attributes
 * @param lng - Target language code (one of SUPPORTED_LANGUAGES: en, ko, pt-BR, es, de, fr, it, nl, sv, pl)
 */
export async function changeLanguage(lng: SupportedLanguage): Promise<void> {
  await i18n.changeLanguage(lng);
  try { localStorage.setItem('heseda-ai-language', lng); } catch { /* quota / blocked */ }
}

/**
 * Get the current language
 */
export function getCurrentLanguage(): SupportedLanguage {
  const lang = i18n.language;
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage) 
    ? (lang as SupportedLanguage) 
    : 'en';
}

export default i18n;
